import * as React from 'react';
import { connect } from 'react-redux';
import { State } from './State';

let phrases: { [key: string]: string } = {};

type PlaceholderValue = number | string;

// This interface can be augmented to allow specific translation keys. 
export interface Translations { }
type TranslationKeys = keyof Translations | (string & {});

// Are you here looking for how we know which language keys exists?
// We find that out dynamically using `webpack/utils/translation-finder.js`
// It goes through all code and automatically makes that phrase available in the frontend code.
export function translate(key: TranslationKeys, args: { [key: string]: PlaceholderValue }): string;
export function translate(key: TranslationKeys, ...values: PlaceholderValue[]): string;
export function translate(key: TranslationKeys) {
  if (!(key in phrases)) {
    if (process.env.NODE_ENV !== 'production') {
      console.error('Missing translation for phrase:', key);
      return key;
    }
    return '';
  } else {
    return format.apply(null, [phrases[key], ...(Array.prototype.slice.call(arguments, 1) as any)]);
  }
}

export function setPhrases(p: { [name: string]: string }) {
  phrases = p;
}

// Format phrase using curly braces, e.g. `format('foo{0} {1}', 'bar', 'baz')`, or `format('foo{x} {y}', { x: 'bar', y: 'baz' })` => Outputs: foobar baz
export function format(phrase: string, args: { [key: string]: PlaceholderValue }): string;
export function format(phrase: string, ...values: PlaceholderValue[]): string;
export function format(phrase: string) {
  if (arguments.length === 1) {
    return phrase;
  } else if (typeof arguments[1] === 'object' && !!arguments[1]) {
    return formatObject(phrase, arguments[1]);
  } else {
    return formatArray(phrase, Array.prototype.slice.call(arguments, 1));
  }
}

function formatArray(phrase: string, values: PlaceholderValue[]) {
  if (!phrase) {
    return phrase;
  }

  const regex = /{(\d+)}/g;
  if (process.env.NODE_ENV !== 'production') {
    const placeholders: any[] = phrase.match(regex) || [];
    if (placeholders.length !== values.length) {
      const error = `The phrase "${phrase}" contains ${placeholders.length} placeholders
                    but ${values.length} placeholders was passed to translate(). The placeholders
                    passed in was: ${JSON.stringify(values)}`;
      throw new Error(error);
    }
  }
  return values.length === 0
    ? phrase
    : phrase.replace(regex, (match: string, index: number) =>
      typeof values[index] !== 'undefined' ? (values[index] as string) : match,
    );
}

export function formatObject(phrase: string, args: { [key: string]: PlaceholderValue }) {
  if (!phrase) {
    return phrase;
  }

  return phrase.replace(/({[^}]+})/g, ph => {
    const key = ph.substr(1, ph.length - 2).trim();
    if (key in args) {
      const val = args[key];
      if (val == null) {
        return '';
      } else if (typeof val === 'number') {
        return val.toString();
      } else {
        return val;
      }
    } else {
      console.error('Could not find placeholder', key, 'in', args);
      return ph;
    }
  });
}

type RequiredProps = {
  phrase: string;
  values?: PlaceholderValue[] | { [key: string]: PlaceholderValue };
  children?: (phrase: string) => React.ReactNode;
};

type ConnectedState = {
  languagePhrases: { [item: string]: string };
};

type Props = RequiredProps & ConnectedState;

function Translate(props: Props) {
  const { phrase, children, values, languagePhrases } = props;
  if (!(phrase in languagePhrases)) {
    if (process.env.NODE_ENV !== 'production') {
      throw new Error(`Missing translation for phrase: ${phrase}`);
    }
  }
  let result = languagePhrases[phrase];
  if (values) {
    result = values instanceof Array ? formatArray(result, values) : formatObject(result, values);
  }
  if (typeof children === 'function') {
    return children(result);
  }
  return result;
}

export default connect(
  (state: State): ConnectedState => ({
    languagePhrases: state.appShellData.languagePhrases,
  }),
)(Translate as React.StatelessComponent<Props>);
